import React, { useState, useEffect } from 'react';
import { 
  Music, 
  RefreshCw, 
  CheckCircle2, 
  Radio,
  PlusCircle,
  Link,
  Share2,
  Volume2,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { TargetPlaylist, MergeResult, PlatformImportLink } from './types/playlist.js';
import { parsePlaylistShareUrl } from './services/importer.js';

export default function App() {
  // Playlist State
  const [targetPlaylists, setTargetPlaylists] = useState<TargetPlaylist[]>([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string>('');
  const [newPlaylistName, setNewPlaylistName] = useState<string>('My Shared Universal Playlist');

  // Import State
  const [shareInput, setShareInput] = useState<string>('https://open.spotify.com/playlist/37i9dQZF1DXcBWIGoYBM5M');
  const [sourcePlatform, setSourcePlatform] = useState<string>('spotify');
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [lastMergeResult, setLastMergeResult] = useState<MergeResult | null>(null);
  const [syncingPlatform, setSyncingPlatform] = useState<string | null>(null);

  const platforms = [
    { id: 'spotify', name: 'Spotify', color: '#22c55e' },
    { id: 'apple', name: 'Apple Music', color: '#ec4899' },
    { id: 'tidal', name: 'Tidal', color: '#06b6d4' },
    { id: 'youtube', name: 'YouTube Music', color: '#ef4444' },
  ];

  // Auto-detect platform from URL input
  const detectedUrl = parsePlaylistShareUrl(shareInput);

  // Fetch playlists from REST API on load & poll periodically for multi-device sync
  const fetchPlaylists = async () => {
    try {
      const res = await fetch('/api/playlists');
      if (res.ok) {
        const data: TargetPlaylist[] = await res.json();
        setTargetPlaylists(data);
        if (data.length > 0 && (!selectedPlaylistId || !data.some((p) => p.id === selectedPlaylistId))) {
          setSelectedPlaylistId(data[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch playlists from server:', err);
    }
  };

  useEffect(() => {
    fetchPlaylists();
    const interval = setInterval(fetchPlaylists, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    try {
      const res = await fetch('/api/playlists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newPlaylistName.trim() }),
      });
      if (res.ok) {
        const created: TargetPlaylist = await res.json();
        await fetchPlaylists();
        setSelectedPlaylistId(created.id);
        setNewPlaylistName('');
      }
    } catch (err) {
      console.error('Failed to create playlist:', err);
    }
  };

  const handleImportPlaylist = async () => {
    if (!selectedPlaylistId || !shareInput.trim()) return;
    try {
      setIsImporting(true);
      setLastMergeResult(null);

      const effectivePlatform = detectedUrl ? detectedUrl.platform : sourcePlatform;

      const res = await fetch(`/api/playlists/${selectedPlaylistId}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceInput: shareInput.trim(),
          fallbackPlatform: effectivePlatform,
        }),
      });

      if (res.ok) {
        const result: MergeResult = await res.json();
        setLastMergeResult(result);
        await fetchPlaylists();
      }
    } catch (err) {
      console.error('Failed to import playlist:', err);
    } finally {
      setIsImporting(false);
    }
  };

  const toggleActivePlayback = async (platform: string) => {
    if (!selectedPlaylistId) return;
    const currentPlaylist = targetPlaylists.find((p) => p.id === selectedPlaylistId);
    const currentlyActive = Boolean(currentPlaylist?.activePlayback[platform]?.isPlaying);

    try {
      const res = await fetch(`/api/playlists/${selectedPlaylistId}/active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform,
          isPlaying: !currentlyActive,
        }),
      });
      if (res.ok) {
        await fetchPlaylists();
      }
    } catch (err) {
      console.error('Failed to toggle playback status:', err);
    }
  };

  const isRealPlatformId = (id?: string) => {
    if (!id) return false;
    return !id.startsWith('sp-playlist-') && !id.startsWith('yt-playlist-') && !id.startsWith('target-') && !id.startsWith('am-playlist-') && !id.startsWith('td-playlist-');
  };

  const getDirectPlatformUrl = (platformId: string, playlist?: TargetPlaylist) => {
    if (!playlist) return 'https://open.spotify.com';
    const existingId = playlist.platformPlaylistIds[platformId];
    const firstTrack = playlist.tracks[0];
    const firstPlatformTrackId = playlist.tracks.map((t) => t.platformIds[platformId]).filter(Boolean)[0];
    const isValidTrackId = firstPlatformTrackId && !firstPlatformTrackId.startsWith('sp-track-') && !firstPlatformTrackId.startsWith('track-');

    switch (platformId) {
      case 'spotify':
        if (isRealPlatformId(existingId)) {
          return `https://open.spotify.com/playlist/${existingId}`;
        }
        if (isValidTrackId) {
          return `https://open.spotify.com/track/${firstPlatformTrackId.replace(/^spotify:track:/, '')}`;
        }
        if (firstTrack) {
          return `https://open.spotify.com/search/${encodeURIComponent(firstTrack.artist + ' ' + firstTrack.title)}`;
        }
        return 'https://open.spotify.com';

      case 'youtube':
        if (isRealPlatformId(existingId)) {
          return `https://music.youtube.com/playlist?list=${existingId}`;
        }
        if (firstPlatformTrackId && /^[a-zA-Z0-9_\-]{11}$/.test(firstPlatformTrackId)) {
          return `https://music.youtube.com/watch?v=${firstPlatformTrackId}`;
        }
        if (firstTrack) {
          return `https://music.youtube.com/search?q=${encodeURIComponent(firstTrack.artist + ' ' + firstTrack.title)}`;
        }
        return 'https://music.youtube.com';

      case 'apple':
        if (isRealPlatformId(existingId)) return `https://music.apple.com/playlist/${existingId}`;
        if (firstTrack) {
          return `https://music.apple.com/us/search?term=${encodeURIComponent(firstTrack.artist + ' ' + firstTrack.title)}`;
        }
        return 'https://music.apple.com';

      case 'tidal':
        if (isRealPlatformId(existingId)) return `https://listen.tidal.com/playlist/${existingId}`;
        if (firstTrack) {
          return `https://listen.tidal.com/search?q=${encodeURIComponent(firstTrack.artist + ' ' + firstTrack.title)}`;
        }
        return 'https://listen.tidal.com';

      default:
        return 'https://open.spotify.com';
    }
  };

  const handleOpenOrSyncPlatform = async (platformId: string) => {
    if (!selectedPlaylistId || !selectedPlaylist) return;
    const existingId = selectedPlaylist.platformPlaylistIds[platformId];

    if (isRealPlatformId(existingId)) {
      window.open(getDirectPlatformUrl(platformId, selectedPlaylist), '_blank');
      return;
    }

    // Trigger on-demand playlist generation & track sync for platform
    try {
      setSyncingPlatform(platformId);
      const res = await fetch(`/api/playlists/${selectedPlaylistId}/sync/${platformId}`, {
        method: 'POST',
      });
      if (res.ok) {
        const { platformPlaylistId } = await res.json();
        await fetchPlaylists();

        const targetUrl = isRealPlatformId(platformPlaylistId)
          ? platformId === 'spotify' 
            ? `https://open.spotify.com/playlist/${platformPlaylistId}`
            : platformId === 'youtube'
            ? `https://music.youtube.com/playlist?list=${platformPlaylistId}`
            : platformId === 'apple'
            ? `https://music.apple.com/playlist/${platformPlaylistId}`
            : `https://listen.tidal.com/playlist/${platformPlaylistId}`
          : getDirectPlatformUrl(platformId, selectedPlaylist);

        window.open(targetUrl, '_blank');
      } else {
        window.open(getDirectPlatformUrl(platformId, selectedPlaylist), '_blank');
      }
    } catch (err) {
      console.error(`Failed to sync playlist to ${platformId}:`, err);
      window.open(getDirectPlatformUrl(platformId, selectedPlaylist), '_blank');
    } finally {
      setSyncingPlatform(null);
    }
  };

  const selectedPlaylist = targetPlaylists.find((p) => p.id === selectedPlaylistId);
  const importLinks: PlatformImportLink[] = selectedPlaylist
    ? platforms.map((p) => {
        const existingId = selectedPlaylist.platformPlaylistIds[p.id];
        const firstTrackId = selectedPlaylist.tracks.map((t) => t.platformIds[p.id]).filter(Boolean)[0];
        return {
          platform: p.id,
          platformName: p.name,
          url: getDirectPlatformUrl(p.id, selectedPlaylist),
          isDirectLink: Boolean(existingId || firstTrackId),
        };
      })
    : [];

  return (
    <div style={{ padding: '24px', maxWidth: '1280px', margin: '0 auto', width: '100%' }}>
      {/* Top Header */}
      <header style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        paddingBottom: '20px', 
        marginBottom: '32px',
        borderBottom: '1px solid var(--border-color)' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #3b82f6, #ec4899)', 
            padding: '10px', 
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Music size={28} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#f8fafc' }}>Universal Shared Playlist Engine</h1>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              On-Demand Playlist Generation &bull; Cross-Device Sync &bull; Spotify &bull; Apple Music &bull; Tidal &bull; YouTube Music
            </p>
          </div>
        </div>
      </header>

      {/* SYNC STUDIO MAIN UI */}
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '24px' }}>
        
        {/* Left Panel: Target Playlist Selector & Share URL Importer */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Target Playlist Selector */}
          <div style={{ 
            background: 'var(--bg-card)', 
            padding: '20px', 
            borderRadius: '16px', 
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Radio size={18} color="#3b82f6" /> 1. Select Target Playlist
            </h2>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                Active Server Target Playlist
              </label>
              <select 
                value={selectedPlaylistId}
                onChange={(e) => setSelectedPlaylistId(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  borderRadius: '8px', 
                  background: 'var(--bg-dark)', 
                  color: '#fff', 
                  border: '1px solid var(--border-color)',
                  fontSize: '14px'
                }}
              >
                {targetPlaylists.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.tracks.length} tracks)
                  </option>
                ))}
              </select>
            </div>

            {/* Create New Target Playlist */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input 
                type="text" 
                placeholder="New Playlist Name..." 
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                style={{ 
                  flex: 1, 
                  padding: '8px 10px', 
                  borderRadius: '6px', 
                  background: 'var(--bg-dark)', 
                  color: '#fff', 
                  border: '1px solid var(--border-color)',
                  fontSize: '13px'
                }}
              />
              <button 
                onClick={handleCreatePlaylist}
                style={{ 
                  background: 'rgba(59, 130, 246, 0.2)', 
                  color: '#3b82f6', 
                  border: '1px solid #3b82f6',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px'
                }}
              >
                <PlusCircle size={14} /> Create
              </button>
            </div>
          </div>

          {/* Smart Playlist URL Importer */}
          <div style={{ 
            background: 'var(--bg-card)', 
            padding: '20px', 
            borderRadius: '16px', 
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Share2 size={18} color="#ec4899" /> 2. Import Playlist URL
              </h2>
              {detectedUrl && (
                <span style={{ 
                  background: detectedUrl.platform === 'spotify' ? 'rgba(34, 197, 94, 0.2)' : detectedUrl.platform === 'apple' ? 'rgba(236, 72, 153, 0.2)' : detectedUrl.platform === 'tidal' ? 'rgba(6, 182, 212, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                  color: detectedUrl.platform === 'spotify' ? '#22c55e' : detectedUrl.platform === 'apple' ? '#ec4899' : detectedUrl.platform === 'tidal' ? '#06b6d4' : '#ef4444',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Sparkles size={12} /> {detectedUrl.platform.toUpperCase()} URL
                </span>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                Paste Playlist Share Link (Spotify, Apple Music, Tidal, YouTube Music)
              </label>
              <input 
                type="text"
                placeholder="https://music.youtube.com/playlist?list=..."
                value={shareInput}
                onChange={(e) => setShareInput(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  borderRadius: '8px', 
                  background: 'var(--bg-dark)', 
                  color: '#fff', 
                  border: '1px solid var(--border-color)',
                  fontSize: '13px',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            {!detectedUrl && (
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                  Fallback Platform
                </label>
                <select 
                  value={sourcePlatform} 
                  onChange={(e) => setSourcePlatform(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '6px', 
                    background: 'var(--bg-dark)', 
                    color: '#fff', 
                    border: '1px solid var(--border-color)',
                    fontSize: '12px'
                  }}
                >
                  {platforms.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            <button 
              onClick={handleImportPlaylist}
              disabled={isImporting || !selectedPlaylistId || !shareInput.trim()}
              style={{ 
                background: isImporting ? '#475569' : 'linear-gradient(135deg, #ec4899, #3b82f6)',
                color: '#fff',
                border: 'none',
                padding: '12px',
                borderRadius: '8px',
                fontWeight: '700',
                cursor: isImporting || !selectedPlaylistId || !shareInput.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '14px'
              }}
            >
              {isImporting ? (
                <>
                  <RefreshCw className="spin" size={16} /> Scanning & Extracting Tracks...
                </>
              ) : (
                <>
                  <Sparkles size={16} /> Scan & Merge Into Target Playlist
                </>
              )}
            </button>
          </div>

          {/* Active Playback / Live Sync Status */}
          <div style={{ 
            background: 'var(--bg-card)', 
            padding: '20px', 
            borderRadius: '16px', 
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Volume2 size={18} color="#22c55e" /> Active Playback Auto-Sync
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
              When active, playlist changes are auto-pushed instantly to playing platforms!
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {platforms.map((p) => {
                const isPlaying = Boolean(selectedPlaylist?.activePlayback[p.id]?.isPlaying);
                return (
                  <button
                    key={p.id}
                    onClick={() => toggleActivePlayback(p.id)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      background: isPlaying ? 'rgba(34, 197, 94, 0.15)' : 'var(--bg-dark)',
                      color: isPlaying ? '#22c55e' : '#fff',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    <span>{p.name}</span>
                    <span>{isPlaying ? '● Playing (Auto-Push ON)' : '○ Idle'}</span>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* Right Panel: Target Playlist Master View, Merge Feedback & Import Links */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Merge Result Alert Banner */}
          {lastMergeResult && (
            <div style={{ 
              background: 'var(--bg-card)', 
              padding: '16px 20px', 
              borderRadius: '12px', 
              border: '1px solid #3b82f6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: '600', color: '#fff', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle2 color="#22c55e" size={18} /> Playlist Scanned & Merged!
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Added <strong style={{ color: '#22c55e' }}>{lastMergeResult.addedTracks.length} new tracks</strong> &bull; Filtered <strong style={{ color: '#ec4899' }}>{lastMergeResult.duplicateTracks.length} duplicates</strong>
                  {lastMergeResult.autoPushedPlatforms.length > 0 && (
                    <span style={{ marginLeft: '8px', color: '#3b82f6' }}>
                      &bull; Auto-pushed to active platforms: {lastMergeResult.autoPushedPlatforms.join(', ')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Platform Import Links Bar */}
          <div style={{ 
            background: 'var(--bg-card)', 
            padding: '20px', 
            borderRadius: '16px', 
            border: '1px solid var(--border-color)' 
          }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Link size={18} color="#3b82f6" /> Universal Platform Import Links
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              {importLinks.map((link) => {
                const themeMap: Record<string, { bg: string; color: string }> = {
                  spotify: { bg: 'linear-gradient(135deg, #1db954, #15803d)', color: '#ffffff' },
                  apple: { bg: 'linear-gradient(135deg, #fc3c44, #c026d3)', color: '#ffffff' },
                  tidal: { bg: 'linear-gradient(135deg, #06b6d4, #0369a1)', color: '#ffffff' },
                  youtube: { bg: 'linear-gradient(135deg, #ef4444, #b91c1c)', color: '#ffffff' },
                };
                const theme = themeMap[link.platform] || { bg: '#3b82f6', color: '#ffffff' };
                const isSyncing = syncingPlatform === link.platform;

                return (
                  <div 
                    key={link.platform}
                    style={{ 
                      background: 'var(--bg-dark)', 
                      padding: '14px', 
                      borderRadius: '12px', 
                      border: '1px solid var(--border-color)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600', fontSize: '13px', color: '#fff' }}>{link.platformName}</span>
                      {link.isDirectLink ? (
                        <span style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>
                          Synced
                        </span>
                      ) : (
                        <span style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>
                          Auto-Create
                        </span>
                      )}
                    </div>

                    <button 
                      onClick={() => handleOpenOrSyncPlatform(link.platform)}
                      disabled={isSyncing}
                      style={{ 
                        width: '100%',
                        background: theme.bg,
                        color: theme.color,
                        border: 'none',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: isSyncing ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                        transition: 'transform 0.15s, opacity 0.15s'
                      }}
                    >
                      {isSyncing ? (
                        <>
                          <RefreshCw className="spin" size={14} /> Creating Playlist...
                        </>
                      ) : (
                        <>
                          <ExternalLink size={14} /> Open with {link.platformName}
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Target Playlist Master Tracklist Table */}
          <div style={{ 
            background: 'var(--bg-card)', 
            padding: '24px', 
            borderRadius: '16px', 
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600' }}>
                Target Playlist Master Tracklist ({selectedPlaylist?.tracks.length || 0} Tracks)
              </h2>
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Target ID: <code style={{ color: '#3b82f6' }}>{selectedPlaylist?.id}</code>
              </span>
            </div>

            {!selectedPlaylist || selectedPlaylist.tracks.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px', 
                color: 'var(--text-secondary)',
                border: '2px dashed var(--border-color)',
                borderRadius: '12px'
              }}>
                <Music size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p>Target playlist is currently empty. Paste a share URL on the left to scan and add songs.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      <th style={{ padding: '12px' }}>#</th>
                      <th style={{ padding: '12px' }}>Track</th>
                      <th style={{ padding: '12px' }}>ISRC Key</th>
                      <th style={{ padding: '12px' }}>Spotify ID</th>
                      <th style={{ padding: '12px' }}>Apple ID</th>
                      <th style={{ padding: '12px' }}>Tidal ID</th>
                      <th style={{ padding: '12px' }}>YouTube ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPlaylist.tracks.map((track, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '12px', color: 'var(--text-secondary)', fontSize: '12px' }}>{idx + 1}</td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '600', color: '#fff' }}>{track.title}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{track.artist}</div>
                        </td>
                        <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#3b82f6' }}>
                          {track.isrc}
                        </td>
                        <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#22c55e' }}>
                          {track.platformIds['spotify'] || '—'}
                        </td>
                        <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#ec4899' }}>
                          {track.platformIds['apple'] || '—'}
                        </td>
                        <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#06b6d4' }}>
                          {track.platformIds['tidal'] || '—'}
                        </td>
                        <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#ef4444' }}>
                          {track.platformIds['youtube'] || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
